import * as PIXI from 'pixi.js';
import dragonBones from "pixi-dragonbones";
import GameObject from '../../GameObject/GameObject';
import IRenderer from './IRenderer';
import TransformComponent from '../../Component/Transform';
import Game from '../../Game';
import ViewportComponent from '../../Component/Map/Viewport';
import Viewport from 'pixi-viewport';
import '@sgorg/pixi-ui';
import IPIXIGameObject from './IPIXIGameObject';
import {
    defaultCanvasSize
} from '../../Constants/Canvas';
import Point from '../../Utility/Point';
import WorldMap from '../../Prototype/World/WorldMap';
import {track} from '../../Utility/Tracking/Track';
import {report} from '../../report';
import {GameScene} from "State/Game/GameScene";
import { IGameConfig } from 'Utility/IGameConfig';

export default class PIXIRenderer implements IRenderer {
    public static instance: PIXIRenderer;
    public static renderer: PIXI.WebGLRenderer | PIXI.CanvasRenderer;
    public static sceneContainer: PIXI.Container;
    //public static debugGraphics: PIXI.Graphics;
    public static rootContainer: PIXI.Container;
    public static mapViewport: Viewport;
    public static dbFactory: dragonBones.PixiFactory;

    public sceneContainer: PIXI.Container;
    public static viewports: Viewport[];
    public static elements: PIXI.ui.Element[];

    /** @deprecated */
    public static readonly borderHeight: number = 0;

    public addGameObject(gameObject: IPIXIGameObject): void {
        if (!gameObject.parent) {
            this.sceneContainer.addChild(gameObject.container);
        }

        const elements: PIXI.ui.Element[] = PIXIRenderer.getChildrenByType(
            gameObject.container,
            PIXI.ui.Element
        );
        if (gameObject.container instanceof PIXI.ui.Element) {
            elements.push(gameObject.container);
            PIXIRenderer.elements.push(gameObject.container);
        }

        elements.forEach(e => {
            e.resize(PIXIRenderer.renderer.width, PIXIRenderer.renderer.height);
        });

        this.addViewports(gameObject);
    }

    private addViewport(viewport: Viewport) {
        if (PIXIRenderer.viewports.indexOf(viewport) === -1)
            PIXIRenderer.viewports.push(viewport);
    }

    private addViewports(gameObject: GameObject) {
        const viewportComponents = gameObject.getComponentsInChildren(
            ViewportComponent
        );
        for (let i = 0, n = viewportComponents.length; i < n; i++) {
            this.addViewport(viewportComponents[i].container);
        }
    }

    public bringToFront(gameObject: GameObject): void {
        if (gameObject && gameObject.container && gameObject.container.parent) {
            gameObject.container.parent.addChild(gameObject.container);
        }
    }

    public destroy(): void {
        this.sceneContainer.destroy();
        this.sceneContainer = null;
    }

    public static getChildrenByType<T extends PIXI.Container>(
        container: PIXI.Container,
        ctor: { new(): T }
    ): T[] {
        return container.children.reduce(
            (acc: T[], val: PIXI.Container) => {
                return acc.concat(PIXIRenderer.getChildrenByType<T>(val, ctor));
            },
            container instanceof ctor ? [container] : []
        );
    }

    public getRenderer() {
        return PIXIRenderer.renderer;
    }

    public static getTexture(texturePath: string): PIXI.Texture {
        const fallback = PIXI.Texture.WHITE;
        if (!texturePath) {
            report('Attempting to retrieve texture without key', {
                once: true,
                sampleRate: 0.1,
                tags: {typeofTexturePath: typeof texturePath},
            });
            return fallback;
        }
        if (Game.resource.existsAndLoaded(texturePath)) {
            return Game.resource.get<PIXI.Texture>(texturePath);
        } else {
            const resource = PIXI.loaders.shared.resources[texturePath];
            if (resource && !resource.isLoading) {
                return resource.texture
            }
            else {
                report("Attempting to retrieve a texture that wasn't loaded", {
                    once: true,
                    sampleRate: 0.1,
                    tags: {
                        texturePath,
                        loadingScheduled: Game.resource.existsOrQueued(texturePath),
                    },
                    fingerprint: 'textureNotLoaded',
                });
            }
            return fallback;
        }
    }

    public static displayObjectToBase64(
        displayObject: PIXI.DisplayObject | PIXI.RenderTexture,
        format: IGameConfig['shareImageFormat'] = Game.config.shareImageFormat,
        quality: number = Game.config.shareImageQuality
    ): string {
        return PIXIRenderer.renderer.plugins.extract.base64(
            displayObject,
            format,
            quality
        );
    }

    public init(sceneName: string, sceneIndex: number): void {
        PIXIRenderer.renderer.view.style.height = "auto";
        this.sceneContainer = new PIXI.Container();
        this.sceneContainer.name = sceneName;

        this.createRendererIfNeeded();

        if (!PIXIRenderer.rootContainer) {
            PIXIRenderer.rootContainer = new PIXI.Container();
            PIXIRenderer.rootContainer.name = 'RootContainer';
            PIXIRenderer.sceneContainer = new PIXI.Container();
            PIXIRenderer.sceneContainer.name = 'SceneContainer';
            PIXIRenderer.rootContainer.addChild(PIXIRenderer.sceneContainer);
        }

        PIXIRenderer.sceneContainer.addChildAt(
            this.sceneContainer,
            Math.min(PIXIRenderer.sceneContainer.children.length, sceneIndex)
        );
    }

    private createRendererIfNeeded() {
        if (PIXIRenderer.renderer) return;

        PIXI.glCore.VertexArrayObject.FORCE_NATIVE = true;
        PIXIRenderer.renderer = PIXI.autoDetectRenderer(
            window.innerWidth,
            window.innerHeight,
            {
                view: document.getElementById('canvas') as HTMLCanvasElement,
                clearBeforeRender: false,
                roundPixels: true,
                legacy: true,
                autoResize: false,
                transparent: true
            },
            Game.config.forceCanvas
        );

        this.trackRendererType(PIXIRenderer.renderer);

        PIXI.settings.MESH_CANVAS_PADDING = 2;

        // Disable default ticker.
        const ticker = PIXI.ticker.shared;
        ticker.autoStart = false;
        ticker.stop();

        PIXIRenderer.renderer.plugins.interaction.autoPreventDefault = true;

        PIXIRenderer.renderer.view.className = 'renderer';
        PIXIRenderer.renderer.backgroundColor = 0xffffff;
        PIXIRenderer.dbFactory = dragonBones.PixiFactory.factory;

        // Canvas drag fix for Edge.
        PIXIRenderer.renderer.view.ondragstart = () => false;
    }

    private trackRendererType(
        renderer: PIXI.WebGLRenderer | PIXI.CanvasRenderer
    ) {
        try {
            const isCanvasRenderer = renderer instanceof PIXI.CanvasRenderer;
            const isWebGLRenderer = renderer instanceof PIXI.WebGLRenderer;
            if (isCanvasRenderer === isWebGLRenderer) {
                report('Failed to parse renderer type', {
                    once: true,
                    sampleRate: 0.1,
                    tags: {
                        isCanvasRenderer,
                        isWebGLRenderer,
                    },
                });
            } else {
                track('renderer', isWebGLRenderer ? 'webgl' : 'canvas');
            }
        } catch (error) {
            report('Failed to track renderer type', {
                error,
                once: true,
                sampleRate: 0.1,
            });
        }
    }

    public removeGameObject(gameObject: GameObject): void {
        if (!gameObject.parent)
            this.sceneContainer.removeChild(gameObject.container);

        if (gameObject.container instanceof PIXI.ui.Element) {
            const i = PIXIRenderer.elements.indexOf(gameObject.container);
            if (i >= 0) PIXIRenderer.elements.splice(i, 1);
        }

        this.removeViewports(gameObject);
    }

    private removeViewport(viewport: Viewport) {
        const index = PIXIRenderer.viewports.indexOf(viewport);
        if (index >= 0) PIXIRenderer.viewports.splice(index, 1);
    }

    private removeViewports(gameObject: GameObject) {
        const viewportComponents = gameObject.getComponentsInChildren(
            ViewportComponent
        );
        for (let i = 0, n = viewportComponents.length; i < n; i++) {
            this.removeViewport(viewportComponents[i].container);
        }
    }

    public static render() {
        try {
            PIXIRenderer.renderer.render(PIXIRenderer.rootContainer);
        } catch (error) {
            // Skip "TypeError: t._frame.clone is not a function" error from older devices.
            // Canvas and bomb card explosion.
            report('render crashed', {
                error,
                sampleRate: 0,
                once: true,
            });
        }
    }

    public static getOffsetY(): number {
        return (PIXIRenderer.renderer.height - defaultCanvasSize.y) / 2;
    }

    public resize(): void {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const GAME_WIDTH = defaultCanvasSize.x;
        const GAME_HEIGHT = defaultCanvasSize.y;
        const RATIO = GAME_WIDTH / GAME_HEIGHT;

        let newWidth: number;
        let newHeight: number;
        let top: number = 0;

        if (height > width) {
            // portrait mode
            newWidth = GAME_WIDTH;
            newHeight = GAME_HEIGHT;

            const scaleFactor: number = Math.min(
                width / GAME_WIDTH,
                height / GAME_HEIGHT
            );
            top = Math.floor((height - GAME_HEIGHT * scaleFactor) / 2);
        } else {
            // landscape mode
            if (width / height >= RATIO) {
                newHeight = GAME_HEIGHT;
                newWidth = Math.floor(newHeight * (width / height));
            } else {
                newWidth = GAME_WIDTH;
                newHeight = Math.floor(newWidth * (height / width));
            }
        }

        PIXIRenderer.renderer.view.style.top = `${top}px`;
        PIXIRenderer.renderer.resize(newWidth, newHeight);
        PIXIRenderer.resizeViewport(width, height);

        if (PIXIRenderer.rootContainer) {
            PIXIRenderer.resizeUI(newWidth, newHeight);
            PIXIRenderer.rootContainer.emit('resized', {
                CANVAS_WIDTH: width,
                CANVAS_HEIGHT: height,
            });
        }
    }

    public static resizeUI(width: number, height: number): void {
        const elements = PIXIRenderer.getChildrenByType(
            PIXIRenderer.rootContainer,
            PIXI.ui.Element
        );
        for (let i = 0, n = elements.length; i < n; i++) {
            const element = elements[i];
            if (!(element.parent instanceof PIXI.ui.Element)) {
                element.resize(width, height);
            }
        }
    }

    public static resizeViewport(width: number, height: number) {
        // Extra-special viewport resizing for world map.
        if (
            !PIXIRenderer.mapViewport?.transform ||
            Game.store.getState().activeScene.result !== GameScene.WORLD
        ) return;

        const yRatio = PIXIRenderer.renderer.height / WorldMap.Height;
        PIXIRenderer.mapViewport.scale.set(yRatio);
        PIXIRenderer.mapViewport.resize(
            PIXIRenderer.renderer.width,
            PIXIRenderer.renderer.height,
            Math.max(WorldMap.Width, PIXIRenderer.renderer.width),
            WorldMap.Height
        );

    }

    public sortChildren(gameObject: GameObject): void {
        if (gameObject.container) {
            gameObject.container.children
                .sort((a, b) => b.position.y - a.position.y)
                .forEach(child => {
                    gameObject.container.addChildAt(child, 0);
                });
        }
    }

    public static spriteToBase64(sprite: PIXI.Sprite): string {
        return PIXIRenderer.renderer.plugins.extract.base64(sprite);
    }

    public start(): void {
        // Static viewports.
        PIXIRenderer.viewports = [];
        // Static elements.
        PIXIRenderer.elements = [];

        this.createRendererIfNeeded();

        //this.resize();
    }

    public stop(): void {
    }

    public static textureToBase64(textureString: string): string {
        const texture: PIXI.Texture = PIXIRenderer.getTexture(textureString);

        if (texture) {
            const tempSprite = new PIXI.Sprite(texture);
            const base64Sprite = PIXIRenderer.renderer.plugins.extract.base64(
                tempSprite
            );
            tempSprite.destroy();

            return base64Sprite;
        } else {
            return '';
        }
    }

    public update(): void {
        const delta = Game.ticker.getDeltaTime();

        // Update viewports manually instead of by the default shared PIXI ticker.
        for (let i = 0, n = PIXIRenderer.viewports.length; i < n; i++) {
            // @ts-ignore
            PIXIRenderer.viewports[i].update(delta);
        }

        for (let i = 0, n = PIXIRenderer.elements.length; i < n; i++) {
            // @ts-ignore
            PIXIRenderer.elements[i].update(delta);
        }

        PIXIRenderer.dbFactory.dragonBones.advanceTime(delta * 0.001);

        PIXIRenderer.render();
    }

    public static getGlobalPosition(gameObject: GameObject): Point {
        let position: Point = new Point(0, 0);

        if (gameObject.hasComponent(TransformComponent)) {
            position = gameObject
                .getComponent(TransformComponent)
                .position.clone() as Point;
        }

        if (gameObject.parent) {
            const parentPosition: Point = PIXIRenderer.getGlobalPosition(
                gameObject.parent
            );
            return new Point(
                position.x + parentPosition.x,
                position.y + parentPosition.y
            );
        }
        return position;
    }

}
